/**
 * ScreenHead — the one page head (`STYLE.md` §6).
 *
 * **The problem it exists for.** On 2026-09-16 a review of every stylesheet in `src/` found
 * three page-title styles in one product — display uppercase 18px on the drawings register,
 * sans 20px on the people screen, sans 26px on the settings pages — and twelve separate
 * copies of the header row that carries them, each with its own gap, its own wrap behaviour
 * and its own idea of where the primary action sits. Every one of those was a reasonable
 * local decision. Identity is not a local decision, so the head stops being one.
 *
 * **What it draws**, in the order a reader meets it:
 *
 * | Slot | Type | Style |
 * |---|---|---|
 * | `title` | required | 18/24 `--font-display` 700, 0.02em, uppercase, `--neutral-900` |
 * | `subtitle` | optional | 13/18 `--neutral-500` — a sentence about the screen |
 * | `meta` | optional | mono 12 `--neutral-500` — the count line, because a count is an |
 * | | | identifier the system produced, not prose |
 * | `actions` | optional | right-aligned, wrapping under the title block when the window |
 * | | | is too narrow to hold both |
 *
 * **`level` is required and has no default, deliberately.** A workspace page is the `h1` of
 * its document; a page *inside* a project is not, because the project shell's tab row already
 * carries the project's name as the `h1` above it. Defaulting to either one would be right on
 * half the screens and silently wrong on the other half — a duplicated `h1` or a document
 * whose outline starts at `h2` — and neither is visible to the person who wrote the screen.
 * Being asked the question is the point.
 *
 * `title` is a `string` rather than a `ReactNode` for the same reason: a head that can hold
 * arbitrary markup is a head that will eventually hold a second style. A name being edited in
 * place is `InlineEdit`'s job (20/28, §2) and it is not this.
 *
 * Every string here is a prop. This component owns no catalog keys — the feature that mounts
 * it resolves its own copy through `useMessages`.
 */

import type { ReactNode } from "react";

import styles from "./ScreenHead.module.css";

export interface ScreenHeadProps {
  /** The screen's name. Drawn uppercase by the stylesheet — pass it in sentence case. */
  readonly title: string;
  /** One sentence about what the screen is for. Optional, and most screens do without. */
  readonly subtitle?: string | undefined;
  /** The count line — "48 drawings, 3 pending". Mono, because a count is a machine's answer. */
  readonly meta?: ReactNode | undefined;
  /** The screen's actions. One primary at most; the rest belong in a `Menu`. */
  readonly actions?: ReactNode | undefined;
  /** `1` on a workspace page, `2` inside a project. See the module note — there is no default. */
  readonly level: 1 | 2;
}

export function ScreenHead({ title, subtitle, meta, actions, level }: ScreenHeadProps) {
  const Heading = level === 1 ? "h1" : "h2";

  return (
    <header className={styles.head}>
      <div className={styles.titles}>
        <Heading className={styles.title}>{title}</Heading>
        {subtitle === undefined ? null : <p className={styles.subtitle}>{subtitle}</p>}
        {meta === undefined ? null : <div className={styles.meta}>{meta}</div>}
      </div>

      {actions === undefined ? null : <div className={styles.actions}>{actions}</div>}
    </header>
  );
}
